import { createContext, useContext } from 'react'
import { createStore, useStore, type StoreApi } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import { DEFAULT_COLUMN_VISIBILITY, sanitizeColumnVisibility, type ColumnId } from '../lib/columns'
import type { EditingCell } from '../lib/permissions'

export interface BudgetGridState {
  projectId: string
  // persisted per project
  collapsedIds: string[]
  columnVisibility: Record<ColumnId, boolean>
  // session only
  rootId: string | null
  search: string
  editing: EditingCell | null
  toggleCollapsed: (id: string) => void
  setCollapsed: (ids: string[], collapsed: boolean) => void
  toggleColumn: (id: ColumnId) => void
  setRootId: (id: string | null) => void
  setSearch: (q: string) => void
  setEditing: (cell: EditingCell | null) => void
}

export const budgetGridStorageKey = (projectId: string) => `urkla.costControl.budgetGrid.${projectId}`

/** Persisted data from an older build or a hand-edited localStorage: keep what is valid, default the rest. */
export const mergePersisted = (persisted: unknown, current: BudgetGridState): BudgetGridState => {
  const p = persisted && typeof persisted === 'object' ? (persisted as Record<string, unknown>) : {}
  return {
    ...current,
    collapsedIds: Array.isArray(p.collapsedIds) ? p.collapsedIds.filter((x): x is string => typeof x === 'string') : [],
    columnVisibility: sanitizeColumnVisibility(p.columnVisibility),
  }
}

export const createBudgetGridStore = (projectId: string): StoreApi<BudgetGridState> =>
  createStore<BudgetGridState>()(
    persist(
      (set) => ({
        projectId,
        collapsedIds: [],
        columnVisibility: { ...DEFAULT_COLUMN_VISIBILITY },
        rootId: null,
        search: '',
        editing: null,
        toggleCollapsed: (id) => set((s) => ({
          collapsedIds: s.collapsedIds.includes(id) ? s.collapsedIds.filter((x) => x !== id) : [...s.collapsedIds, id],
        })),
        setCollapsed: (ids, collapsed) => set((s) => {
          if (!collapsed) {
            const drop = new Set(ids)
            return { collapsedIds: s.collapsedIds.filter((x) => !drop.has(x)) }
          }
          const next = new Set(s.collapsedIds)
          ids.forEach((x) => next.add(x))
          return { collapsedIds: Array.from(next) }
        }),
        toggleColumn: (id) => set((s) => ({ columnVisibility: { ...s.columnVisibility, [id]: !s.columnVisibility[id] } })),
        setRootId: (rootId) => set({ rootId, editing: null }),
        setSearch: (search) => set({ search, editing: null }),
        setEditing: (editing) => set({ editing }),
      }),
      {
        name: budgetGridStorageKey(projectId),
        storage: createJSONStorage(() => localStorage),
        version: 1,
        partialize: (s) => ({ collapsedIds: s.collapsedIds, columnVisibility: s.columnVisibility }),
        merge: (persisted, current) => mergePersisted(persisted, current),
      },
    ),
  )

export const BudgetGridStoreContext = createContext<StoreApi<BudgetGridState> | null>(null)

export function useBudgetGridStore<T>(selector: (s: BudgetGridState) => T): T {
  const api = useContext(BudgetGridStoreContext)
  if (!api) throw new Error('useBudgetGridStore must be used inside BudgetGridStoreContext.Provider')
  return useStore(api, selector)
}
